import { formatE164Display, toE164 } from '@lsc/domain';

export function processorName(slug: string): string {
  switch (slug) {
    case 'vivid':
      return 'Vivid';
    case 'green_payments':
      return 'Green Payments';
    case 'epi':
      return 'EPI';
    default:
      return slug;
  }
}

export function phone(e164: string | null | undefined): string | null {
  if (!e164) return null;
  // A hand-typed number is stored as typed ("3106172950"); show it the same way as an E.164 one.
  return formatE164Display(toE164(e164) ?? e164);
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "2026-10-05" or an ISO timestamp to "Oct 5, 2026" (UTC calendar day, which is how the processors report). */
export function day(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return iso;
  const [, y, mo, d] = m;
  return `${MONTHS[Number(mo) - 1] ?? mo} ${Number(d)}, ${y}`;
}

/** Days between a calendar day and today, for "last batch 3 days ago". */
export function daysAgo(iso: string | null | undefined, now = new Date()): number | null {
  if (!iso) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return null;
  const then = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  return Math.round((today - then) / 86_400_000);
}

export function relativeDays(iso: string | null | undefined): string | null {
  const n = daysAgo(iso);
  if (n === null) return null;
  if (n <= 0) return 'today';
  if (n === 1) return 'yesterday';
  if (n < 30) return `${n} days ago`;
  if (n < 365) return `${Math.round(n / 30)} months ago`;
  return `${(n / 365).toFixed(1)} years ago`;
}

/** Where a ticket sits, as a phrase after the status word: "with Maverick", "on the merchant". */
export const ROUTED_LABEL: Record<string, string> = { EK: 'with EK', Maverick: 'with Maverick', EPI: 'with EPI', 'Green Payments': 'with Green Payments', Onboarding: 'with Onboarding', RMA: 'at RMA', Dev: 'with Dev' };
export const WAITING_LABEL: Record<string, string> = { client: 'on the merchant', team_check: 'team check', approval: 'for approval', processor: 'on the processor', vendor_shipping: 'shipping' };

export function whereIs(routedTo: string | null, waitingOn: string | null): string | null {
  if (routedTo) return ROUTED_LABEL[routedTo] ?? routedTo;
  if (waitingOn) return WAITING_LABEL[waitingOn] ?? waitingOn;
  return null;
}

/** A ticket's age in hours as the board shows it: "just now", "5h", "3d", "2w". */
export function age(hours: number): string {
  if (hours < 1) return 'just now';
  if (hours < 24) return `${hours}h`;
  const d = Math.floor(hours / 24);
  return d < 14 ? `${d}d` : `${Math.floor(d / 7)}w`;
}

/**
 * An ISO timestamp as "Oct 22, 2026, 7:12 PM" in the rep's own time zone (UAT 2026-10-22: it used
 * to print the UTC digits, four hours off for the team). A date with no time stays a calendar day.
 */
export function dayTime(iso: string | null | undefined, timeZone?: string): string | null {
  if (!iso) return null;
  if (!/T\d{2}:\d{2}/.test(iso)) return day(iso);
  const t = new Date(iso);
  if (Number.isNaN(t.getTime())) return day(iso);
  return t.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', ...(timeZone ? { timeZone } : {}) });
}
